import { create } from 'zustand'
import type { Trophy } from '@/milestones/types'
import { key, read, write } from '@/lib/storage'

interface PersistedShape {
  unlocked: Trophy[]
  version: 1
}

interface TrophyState {
  tenantId: string | null
  unlocked: Trophy[]
  initFor(tenantId: string): void
  unlock(milestoneId: string, hitAt?: string): boolean
  unlockMany(milestoneIds: string[]): Trophy[]
  reset(): void
  has(milestoneId: string): boolean
  unlockedSet(): Set<string>
}

function trophyKey(tenantId: string) {
  return key<PersistedShape>('trophies', 1, tenantId)
}

function load(tenantId: string): Trophy[] {
  return read(trophyKey(tenantId))?.unlocked ?? []
}

function save(tenantId: string, list: Trophy[]) {
  write(trophyKey(tenantId), { unlocked: list, version: 1 })
}

export const useTrophyStore = create<TrophyState>((set, get) => ({
  tenantId: null,
  unlocked: [],
  initFor(tenantId) {
    set({ tenantId, unlocked: load(tenantId) })
  },
  unlock(milestoneId, hitAt = new Date().toISOString()) {
    const { tenantId, unlocked } = get()
    if (!tenantId) return false
    if (unlocked.some((t) => t.milestoneId === milestoneId)) return false
    const next = [...unlocked, { milestoneId, hitAt }]
    save(tenantId, next)
    set({ unlocked: next })
    return true
  },
  unlockMany(milestoneIds) {
    const { tenantId, unlocked } = get()
    if (!tenantId) return []
    const existing = new Set(unlocked.map((t) => t.milestoneId))
    const newOnes: Trophy[] = []
    const at = new Date().toISOString()
    for (const id of milestoneIds) {
      if (existing.has(id)) continue
      newOnes.push({ milestoneId: id, hitAt: at })
      existing.add(id)
    }
    if (newOnes.length === 0) return []
    const next = [...unlocked, ...newOnes]
    save(tenantId, next)
    set({ unlocked: next })
    return newOnes
  },
  reset() {
    const { tenantId } = get()
    if (!tenantId) return
    save(tenantId, [])
    set({ unlocked: [] })
  },
  has(milestoneId) {
    return get().unlocked.some((t) => t.milestoneId === milestoneId)
  },
  unlockedSet() {
    return new Set(get().unlocked.map((t) => t.milestoneId))
  },
}))
